import React, { useState } from 'react';
import { Typography, Box, Stack, Chip, Grid, Button, IconButton } from '@mui/material';
import { Star, Info, ShoppingCart, Favorite, Person, Error } from '@mui/icons-material';
import LiveEditor from '../components/Playground/LiveEditor';
import NumericIconCard from '../components/NumericIconCard/NumericIconCard';
import CustomTabs from '../components/CustomTabs/CustomTabs';
import CustomTable from '../components/Table/CustomTable';
import AutobiaBadge, { AutobiaBadgeCounter } from '../components/DataDisplay/AutobiaBadge';
import AutobiaTooltip, { AutobiaTooltipRich } from '../components/DataDisplay/AutobiaTooltip';
import { AutobiaMenuList, AutobiaContactList, AutobiaSettingsList } from '../components/DataDisplay/AutobiaList';
import AutobiaPagination, { AutobiaSimplePagination, AutobiaTablePagination } from '../components/Navigation/AutobiaPagination';
import AutobiaDataTable from '../components/DataDisplay/AutobiaDataTable';
import AutobiaInformationDisplay from '../components/DataDisplay/AutobiaInformationDisplay';
import ComponentDemo from '../components/ComponentDemo/ComponentDemo';
import ResponsiveTableWrapper from '../components/ResponsiveTableWrapper';

const DataDisplay = () => {
  const [selectedTab, setSelectedTab] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Sample data
  const tabsData = [
    { id: 1, label: 'Orders' },
    { id: 2, label: 'Transactions' },
    { id: 3, label: 'Reports' },
  ];

  const tableColumns = [
    { id: 'id', label: 'ID' },
    { id: 'name', label: 'Product Name' },
    { id: 'amount', label: 'Amount' },
    { id: 'status', label: 'Status' },
    { id: 'date', label: 'Date' },
  ];

  const tableRows = [
    { 
      id: 'ORD-001', 
      name: 'Premium Laptop', 
      amount: '3,250.00', 
      status: 'Completed',
      date: '2024-01-15'
    },
    { 
      id: 'ORD-002', 
      name: 'Gaming Mouse', 
      amount: '89.50', 
      status: 'Pending',
      date: '2024-01-14'
    },
    { 
      id: 'ORD-003', 
      name: 'Wireless Keyboard', 
      amount: '150.75', 
      status: 'Processing',
      date: '2024-01-14'
    },
    { 
      id: 'ORD-004', 
      name: 'Monitor Stand', 
      amount: '45.00', 
      status: 'Shipped',
      date: '2024-01-13'
    },
    { 
      id: 'ORD-005', 
      name: 'USB Cable', 
      amount: '12.99', 
      status: 'Delivered',
      date: '2024-01-12'
    },
  ];

  // Placeholder icons with Autobia colors
  const iconSvg = (color) => `data:image/svg+xml,%3Csvg width='64' height='64' viewBox='0 0 64 64' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='64' height='64' rx='32' fill='${encodeURIComponent(color)}'/%3E%3Cpath d='M32 20v24M20 32h24' stroke='white' stroke-width='2' stroke-linecap='round'/%3E%3C/svg%3E`;

  // Component Examples
  const NumericCardsExample = () => (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6} lg={3}>
        <NumericIconCard
          title="Total Revenue"
          value={245680}
          imageSrc={iconSvg('#FA9940')}
        />
      </Grid>
      <Grid item xs={12} sm={6} lg={3}>
        <NumericIconCard
          title="Active Orders"
          value={1247}
          imageSrc={iconSvg('#4A60F4')}
        />
      </Grid>
      <Grid item xs={12} sm={6} lg={3}>
        <NumericIconCard
          title="Pending Payment"
          value={8950}
          imageSrc={iconSvg('#FACC15')}
        />
      </Grid>
      <Grid item xs={12} sm={6} lg={3}>
        <NumericIconCard
          title="Completed"
          value={15680}
          imageSrc={iconSvg('#12B76A')}
        />
      </Grid>
    </Grid>
  );

  const TabsExample = () => (
    <Box sx={{ 
      backgroundColor: 'background.paper', 
      p: 3, 
      borderRadius: 2, 
      border: '1px solid', 
      borderColor: 'gray.g200',
      minHeight: '200px'
    }}>
      <CustomTabs
        tabsData={tabsData}
        value={selectedTab}
        onChange={setSelectedTab}
      />
      <Box sx={{ mt: 3, p: 2, backgroundColor: 'gray.g25', borderRadius: 1 }}>
        <Typography variant="body1">
          Content for {tabsData.find(tab => tab.id === selectedTab)?.label} tab
        </Typography>
        <Typography variant="body2" color="gray.g600" sx={{ mt: 1 }}>
          This is where the tab content would be displayed. Click different tabs to see the active state change.
        </Typography>
      </Box>
    </Box>
  );

  const TableExample = () => (
    <Box sx={{ 
      backgroundColor: 'background.paper', 
      p: { xs: 1, sm: 2 }, 
      borderRadius: 2, 
      border: '1px solid', 
      borderColor: 'gray.g200',
      width: '100%',
      overflow: 'hidden'
    }}>
      <ResponsiveTableWrapper>
        <CustomTable
          tableColumns={tableColumns}
          tableRows={tableRows}
          fetchingStatus="succeeded"
          currentTab={1}
        />
      </ResponsiveTableWrapper>
    </Box>
  );

  const LoadingTableExample = () => (
    <Box sx={{ 
      backgroundColor: 'background.paper', 
      p: { xs: 1, sm: 2 }, 
      borderRadius: 2, 
      border: '1px solid', 
      borderColor: 'gray.g200',
      width: '100%',
      overflow: 'hidden'
    }}>
      <ResponsiveTableWrapper>
        <CustomTable
          tableColumns={tableColumns}
          tableRows={[]}
          fetchingStatus="loading"
          currentTab={1}
        />
      </ResponsiveTableWrapper>
    </Box>
  );

  const EmptyTableExample = () => (
    <Box sx={{ 
      backgroundColor: 'background.paper', 
      p: { xs: 1, sm: 2 }, 
      borderRadius: 2, 
      border: '1px solid', 
      borderColor: 'gray.g200',
      width: '100%',
      overflow: 'hidden'
    }}>
      <ResponsiveTableWrapper>
        <CustomTable
          tableColumns={tableColumns}
          tableRows={[]}
          fetchingStatus="succeeded"
          currentTab={1}
        />
      </ResponsiveTableWrapper>
    </Box>
  );

  // Badge Examples
  const BadgeExample = () => (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6}>
        <Typography variant="h6" sx={{ mb: 2 }}>Basic Badges</Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <AutobiaBadge label="Primary" />
          <AutobiaBadge label="Secondary" color="secondary" />
          <AutobiaBadge label="Success" color="success" />
          <AutobiaBadge label="Error" color="error" />
          <AutobiaBadge label="Warning" color="warning" />
          <AutobiaBadge label="Info" color="info" />
          <AutobiaBadge label="Gray" color="gray" />
        </Stack>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Typography variant="h6" sx={{ mb: 2 }}>Outlined Badges</Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <AutobiaBadge label="Primary" variant="outlined" />
          <AutobiaBadge label="Secondary" color="secondary" variant="outlined" />
          <AutobiaBadge label="Success" color="success" variant="outlined" />
          <AutobiaBadge label="Error" color="error" variant="outlined" />
        </Stack>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Typography variant="h6" sx={{ mb: 2 }}>Interactive Badges</Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <AutobiaBadge label="Clickable" clickable onClick={() => alert('Badge clicked!')} />
          <AutobiaBadge label="With Icon" icon={<Star />} />
          <AutobiaBadge label="Deletable" onDelete={() => alert('Badge deleted!')} />
          <AutobiaBadge label="Disabled" disabled />
        </Stack>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Typography variant="h6" sx={{ mb: 2 }}>Counter Badges</Typography>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
          <Box sx={{ position: 'relative' }}>
            <IconButton>
              <ShoppingCart />
            </IconButton>
            <Box sx={{ position: 'absolute', top: -8, right: -8 }}>
              <AutobiaBadgeCounter count={3} />
            </Box>
          </Box>
          <Box sx={{ position: 'relative' }}>
            <IconButton>
              <Favorite />
            </IconButton>
            <Box sx={{ position: 'absolute', top: -8, right: -8 }}>
              <AutobiaBadgeCounter count={99} color="error" />
            </Box>
          </Box>
          <Box sx={{ position: 'relative' }}>
            <IconButton>
              <Person />
            </IconButton>
            <Box sx={{ position: 'absolute', top: -8, right: -8 }}>
              <AutobiaBadgeCounter count={150} max={99} color="secondary" />
            </Box>
          </Box>
        </Stack>
      </Grid>
    </Grid>
  );

  const TooltipExample = () => (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6}>
        <Typography variant="h6" sx={{ mb: 2 }}>Basic Tooltips</Typography>
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          <AutobiaTooltip title="This is a primary tooltip">
            <Button variant="contained">Hover me</Button>
          </AutobiaTooltip>
          <AutobiaTooltip title="Secondary color tooltip" color="secondary">
            <Button variant="outlined" color="secondary">Secondary</Button>
          </AutobiaTooltip>
          <AutobiaTooltip title="Light tooltip with border" color="light">
            <Button variant="outlined">Light</Button>
          </AutobiaTooltip>
        </Stack>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Typography variant="h6" sx={{ mb: 2 }}>Tooltip Colors</Typography>
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          <AutobiaTooltip title="Success message" color="success">
            <IconButton color="success"><Star /></IconButton>
          </AutobiaTooltip>
          <AutobiaTooltip title="Error notification" color="error">
            <IconButton color="error"><Error /></IconButton>
          </AutobiaTooltip>
          <AutobiaTooltip title="Warning alert" color="warning">
            <IconButton sx={{ color: 'warning.main' }}><Info /></IconButton>
          </AutobiaTooltip>
        </Stack>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Typography variant="h6" sx={{ mb: 2 }}>Tooltip Sizes & Placement</Typography>
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          <AutobiaTooltip title="Small tooltip" size="small" placement="top">
            <Button size="small">Small</Button>
          </AutobiaTooltip>
          <AutobiaTooltip title="Medium tooltip (default)" size="medium" placement="right">
            <Button>Medium</Button>
          </AutobiaTooltip>
          <AutobiaTooltip title="Large tooltip with more detailed information" size="large" placement="bottom">
            <Button size="large">Large</Button>
          </AutobiaTooltip>
        </Stack>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Typography variant="h6" sx={{ mb: 2 }}>Rich Content Tooltip</Typography>
        <AutobiaTooltipRich
          title="Order Information"
          subtitle="Click to view detailed order status and shipping information"
          color="primary"
        >
          <Button variant="contained" startIcon={<Info />}>
            Order Details
          </Button>
        </AutobiaTooltipRich>
      </Grid>
    </Grid>
  );

  // List Examples
  const ListExample = () => {
    const menuItems = [
      { id: 1, icon: <ShoppingCart />, primary: 'Orders', secondary: 'Manage your orders', navigatable: true },
      { id: 2, icon: <Person />, primary: 'Customers', secondary: 'Customer management', navigatable: true },
      { id: 3, icon: <Info />, primary: 'Reports', secondary: 'View analytics', navigatable: true },
      { id: 4, icon: <Star />, primary: 'Reviews', secondary: 'Customer feedback', navigatable: true },
    ];

    const contacts = [
      { id: 1, name: 'Ahmed Al-Rashid', email: 'ahmed@example.com', avatar: '', status: 'Online' },
      { id: 2, name: 'Sarah Johnson', email: 'sarah@example.com', avatar: '', status: 'Away' },
      { id: 3, name: 'Mohammed Hassan', email: 'mohammed@example.com', avatar: '', status: 'Offline' },
    ];

    const settings = [
      { id: 1, icon: <Person />, title: 'Profile Settings', description: 'Update your profile information' },
      { id: 2, icon: <ShoppingCart />, title: 'Order Preferences', description: 'Manage order notifications' },
      { id: 3, icon: <Info />, title: 'Privacy', description: 'Control your privacy settings' },
    ];

    return (
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Typography variant="h6" sx={{ mb: 2 }}>Menu List</Typography>
          <AutobiaMenuList items={menuItems} />
        </Grid>
        <Grid item xs={12} md={4}>
          <Typography variant="h6" sx={{ mb: 2 }}>Contact List</Typography>
          <AutobiaContactList contacts={contacts} />
        </Grid>
        <Grid item xs={12} md={4}>
          <Typography variant="h6" sx={{ mb: 2 }}>Settings List</Typography>
          <AutobiaSettingsList settings={settings} />
        </Grid>
      </Grid>
    );
  };

  const PaginationExample = () => {
    const totalItems = 250;
    const totalPages = Math.ceil(totalItems / pageSize);

    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="h6" sx={{ mb: 2 }}>Complete Pagination</Typography>
          <AutobiaPagination
            count={totalPages}
            page={currentPage}
            onChange={(e, page) => setCurrentPage(page)}
            pageSize={pageSize}
            onPageSizeChange={setPageSize}
            totalItems={totalItems}
            showFirstLast={true}
            showInfo={true}
            showPageSize={true}
          />
        </Grid>
        
        <Grid item xs={12}>
          <Typography variant="h6" sx={{ mb: 2 }}>Simple Pagination</Typography>
          <AutobiaSimplePagination
            count={totalPages}
            page={currentPage}
            onChange={(e, page) => setCurrentPage(page)}
          />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="h6" sx={{ mb: 2 }}>Table Pagination</Typography>
          <Box sx={{ border: '1px solid', borderColor: 'gray.g200', borderRadius: 1 }}>
            <Box sx={{ p: { xs: 2, sm: 3 }, borderBottom: '1px solid', borderColor: 'gray.g200' }}>
              <Typography>Table Content Area</Typography>
              <Typography variant="body2" color="gray.g600">
                This would be your table content...
              </Typography>
            </Box>
            <AutobiaTablePagination
              count={totalPages}
              page={currentPage}
              onChange={(e, page) => setCurrentPage(page)}
              totalItems={totalItems}
              pageSize={pageSize}
            />
          </Box>
        </Grid>
      </Grid>
    );
  };

  // New Advanced Components
  const AdvancedTableExample = () => {

    const sampleData = [
      { 
        id: 1, 
        name: 'Ahmed Al-Rashid', 
        company: 'Tech Solutions Ltd', 
        amount: 15750.00, 
        status: 'Active',
        date: '2024-01-15',
        type: 'Premium'
      },
      { 
        id: 2, 
        name: 'Sarah Johnson', 
        company: 'Digital Dynamics', 
        amount: 8950.50, 
        status: 'Pending',
        date: '2024-01-14',
        type: 'Standard'
      },
      { 
        id: 3, 
        name: 'Mohammed Hassan', 
        company: 'Innovation Hub', 
        amount: 22100.75, 
        status: 'Active',
        date: '2024-01-13',
        type: 'Enterprise'
      },
      { 
        id: 4, 
        name: 'Lisa Chen', 
        company: 'Global Systems', 
        amount: 5420.25, 
        status: 'Inactive',
        date: '2024-01-12',
        type: 'Basic'
      },
    ];

    const tableColumns = [
      { id: 'name', label: 'Customer Name', minWidth: 120 },
      { id: 'company', label: 'Company', minWidth: 100 },
      { 
        id: 'amount', 
        label: 'Amount', 
        type: 'currency',
        currency: 'SAR',
        align: 'right',
        minWidth: 90
      },
      { 
        id: 'status', 
        label: 'Status', 
        type: 'chip',
        chipColor: (value) => {
          switch (value) {
            case 'Active': return 'success';
            case 'Pending': return 'warning';
            case 'Inactive': return 'error';
            default: return 'default';
          }
        },
        minWidth: 80
      },
      { id: 'date', label: 'Date', type: 'date', minWidth: 90 },
      { id: 'type', label: 'Type', minWidth: 80 },
    ];

    const tableActions = [
      {
        icon: <Star />,
        label: 'View',
        onClick: (row) => alert(`Viewing ${row.name}`),
        color: 'primary',
      },
      {
        icon: <Info />,
        label: 'Edit',
        onClick: (row) => alert(`Editing ${row.name}`),
        color: 'secondary',
      },
      {
        icon: <Error />,
        label: 'Delete',
        onClick: (row) => alert(`Deleting ${row.name}`),
        color: 'error',
        disabled: (row) => row.status === 'Active',
      },
    ];

    return (
      <ResponsiveTableWrapper>
        <AutobiaDataTable
          data={sampleData}
          columns={tableColumns}
          selectable={true}
          searchable={true}
          exportable={true}
          animated={true}
          actions={tableActions}
          pagination={true}
          onSelectionChange={(rows) => console.log('Selected:', rows)}
          onRowClick={(row) => console.log('Row clicked:', row)}
          maxHeight={500}
        />
      </ResponsiveTableWrapper>
    );
  };

  const InformationDisplayExample = () => {
    const orderInfo = [
      { label: 'Order ID', value: 'ORD-2024-001', type: 'id', copyable: true },
      { label: 'Customer', value: 'Ahmed Al-Rashid', type: 'user' },
      { label: 'Company', value: 'Tech Solutions Ltd', type: 'company' },
      { label: 'Amount', value: 15750.00, type: 'currency', currency: 'SAR' },
      { label: 'Status', value: 'Completed', type: 'status' },
      { label: 'Order Date', value: '2024-01-15T10:30:00Z', type: 'date' },
      { 
        label: 'Payment Link', 
        value: 'https://payment.autobia.sa/pay/ORD-2024-001', 
        type: 'link',
        onClick: (value) => window.open(value, '_blank')
      },
    ];

    const accountDetails = [
      { label: 'Account ID', value: 'ACC-789456', type: 'id' },
      { label: 'Balance', value: 25750.50, type: 'currency' },
      { label: 'Status', value: 'Active', type: 'status' },
      { label: 'Last Transaction', value: '2024-01-15T14:22:00Z', type: 'date' },
    ];

    return (
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <AutobiaInformationDisplay
            title="Order Information"
            data={orderInfo}
            animated={true}
            copyable={true}
            onCopy={(value) => console.log('Copied:', value)}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <AutobiaInformationDisplay
            title="Account Details"
            data={accountDetails}
            variant="compact"
            layout="horizontal"
            showDividers={false}
          />
        </Grid>
        <Grid item xs={12}>
          <AutobiaInformationDisplay
            title="Transaction Summary"
            data={[
              { label: 'Total Transactions', value: 45, type: 'default' },
              { label: 'Successful', value: 42, type: 'success' },
              { label: 'Failed', value: 3, type: 'error' },
              { label: 'Total Volume', value: 125750.25, type: 'currency' },
            ]}
            variant="grid"
            animated={true}
          />
        </Grid>
      </Grid>
    );
  };

  return (
    <Box>
      <Stack spacing={2} sx={{ mb: { xs: 3, sm: 4, md: 6 } }}>
        <Typography variant="h2" fontWeight={700} color="gray.g700">
          Data Display
        </Typography>
        <Typography variant="body1" color="gray.g600" sx={{ maxWidth: 600 }}>
          Components for displaying data, metrics, and structured information 
          with consistent Autobia styling and formatting.
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
          <Chip label="Financial Display" color="primary" variant="outlined" />
          <Chip label="Data Tables" variant="outlined" />
          <Chip label="Tab Navigation" variant="outlined" />
        </Stack>
      </Stack>

      <Stack spacing={{ xs: 4, sm: 5, md: 6 }}>
        {/* Numeric Icon Cards */}
        <LiveEditor
          title="Numeric Icon Cards"
          description="Financial and metric display cards with Saudi Riyal formatting and custom icons."
          component={NumericCardsExample}
          code={`import NumericIconCard from './components/NumericIconCard/NumericIconCard';

// Create custom icons or use your existing icon assets
const revenueIcon = '/icons/revenue.svg';
const ordersIcon = '/icons/orders.svg';
const pendingIcon = '/icons/pending.svg';
const completedIcon = '/icons/completed.svg';

const Example = () => (
  <Grid container spacing={2}>
    <Grid item xs={12} sm={6} lg={3}>
      <NumericIconCard
        title="Total Revenue"
        value={245680}
        imageSrc={revenueIcon}
      />
    </Grid>
    <Grid item xs={12} sm={6} lg={3}>
      <NumericIconCard
        title="Active Orders"
        value={1247}
        imageSrc={ordersIcon}
      />
    </Grid>
    <Grid item xs={12} sm={6} lg={3}>
      <NumericIconCard
        title="Pending Payment"
        value={8950}
        imageSrc={pendingIcon}
      />
    </Grid>
    <Grid item xs={12} sm={6} lg={3}>
      <NumericIconCard
        title="Completed"
        value={15680}
        imageSrc={completedIcon}
      />
    </Grid>
  </Grid>
);

// Features:
// - Automatic thousand separators (1,234)
// - Saudi Riyal symbol integration
// - Responsive grid layout
// - Custom icon support`}
        />

        {/* Tabs */}
        <LiveEditor
          title="Tab Navigation"
          description="Tab component for organizing content sections with active state management."
          component={TabsExample}
          code={`import CustomTabs from './components/CustomTabs/CustomTabs';

const [selectedTab, setSelectedTab] = useState(1);

const tabsData = [
  { id: 1, label: 'Orders' },
  { id: 2, label: 'Transactions' },
  { id: 3, label: 'Reports' },
];

const Example = () => (
  <Box sx={{ backgroundColor: 'background.paper', p: 3, borderRadius: 2 }}>
    <CustomTabs
      tabsData={tabsData}
      value={selectedTab}
      onChange={setSelectedTab}
    />
    <Box sx={{ mt: 3 }}>
      {/* Tab content based on selectedTab */}
      {selectedTab === 1 && <OrdersContent />}
      {selectedTab === 2 && <TransactionsContent />}
      {selectedTab === 3 && <ReportsContent />}
    </Box>
  </Box>
);

// Features:
// - Active state styling
// - Keyboard navigation
// - Customizable tab data
// - Material-UI integration`}
        />

        {/* Data Table */}
        <LiveEditor
          title="Data Table"
          description="Comprehensive data table with sticky headers, responsive design, and loading states."
          component={TableExample}
          code={`import CustomTable from './components/Table/CustomTable';

const tableColumns = [
  { id: 'id', label: 'Order ID' },
  { id: 'name', label: 'Product Name' },
  { id: 'amount', label: 'Amount' },
  { id: 'status', label: 'Status' },
  { id: 'date', label: 'Date' },
];

const tableRows = [
  { 
    id: 'ORD-001', 
    name: 'Premium Laptop', 
    amount: '3,250.00', 
    status: 'Completed',
    date: '2024-01-15'
  },
  // ... more rows
];

const Example = () => (
  <CustomTable
    tableColumns={tableColumns}
    tableRows={tableRows}
    fetchingStatus="succeeded"
    currentTab={1}
  />
);

// Features:
// - Sticky header
// - Responsive scrolling
// - Loading and empty states
// - Custom cell rendering support`}
        />

        {/* Loading Table */}
        <LiveEditor
          title="Table Loading State"
          description="Table component showing loading state with progress indicator."
          component={LoadingTableExample}
          code={`import CustomTable from './components/Table/CustomTable';

const Example = () => (
  <CustomTable
    tableColumns={tableColumns}
    tableRows={[]}
    fetchingStatus="loading"  // Shows loading indicator
    currentTab={1}
  />
);

// Loading state automatically shows:
// - Linear progress bar
// - Proper loading UI
// - Maintains table structure`}
        />

        {/* Empty Table */}
        <LiveEditor
          title="Table Empty State"
          description="Table component showing empty state when no data is available."
          component={EmptyTableExample}
          code={`import CustomTable from './components/Table/CustomTable';

const Example = () => (
  <CustomTable
    tableColumns={tableColumns}
    tableRows={[]}              // Empty data array
    fetchingStatus="succeeded"  // Completed loading
    currentTab={1}
  />
);

// Empty state automatically shows:
// - "No Data Available" message
// - Proper empty state styling
// - Maintains table header structure`}
        />

        {/* Badges */}
        <LiveEditor
          title="Badges & Labels"
          description="Status badges and labels for categorization, notifications, and interactive elements."
          component={BadgeExample}
          code={`import AutobiaBadge, { AutobiaBadgeCounter } from './components/DataDisplay/AutobiaBadge';
import { Star, ShoppingCart } from '@mui/icons-material';

const Example = () => (
  <Grid container spacing={3}>
    <Grid item xs={12}>
      {/* Basic Badges */}
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <AutobiaBadge label="Primary" />
        <AutobiaBadge label="Secondary" color="secondary" />
        <AutobiaBadge label="Success" color="success" />
        <AutobiaBadge label="Error" color="error" />
        <AutobiaBadge label="Warning" color="warning" />
      </Stack>
    </Grid>
    
    <Grid item xs={12}>
      {/* Outlined Badges */}
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <AutobiaBadge label="Primary" variant="outlined" />
        <AutobiaBadge label="Success" color="success" variant="outlined" />
      </Stack>
    </Grid>
    
    <Grid item xs={12}>
      {/* Interactive Badges */}
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <AutobiaBadge label="Clickable" clickable onClick={() => alert('Clicked!')} />
        <AutobiaBadge label="With Icon" icon={<Star />} />
        <AutobiaBadge label="Deletable" onDelete={() => alert('Deleted!')} />
      </Stack>
    </Grid>
    
    <Grid item xs={12}>
      {/* Counter Badges */}
      <Box sx={{ position: 'relative', display: 'inline-block' }}>
        <IconButton>
          <ShoppingCart />
        </IconButton>
        <Box sx={{ position: 'absolute', top: -8, right: -8 }}>
          <AutobiaBadgeCounter count={3} />
        </Box>
      </Box>
    </Grid>
  </Grid>
);

// Props:
// label: string - Badge text
// color: 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info' | 'gray'
// variant: 'filled' | 'outlined' - Badge style
// size: 'small' | 'medium' - Badge size
// clickable: boolean - Makes badge clickable
// onClick: function - Click handler
// onDelete: function - Delete handler (shows X icon)
// disabled: boolean - Disabled state
// icon: ReactNode - Icon to display

// AutobiaBadgeCounter Props:
// count: number - Number to display
// max: number - Maximum number to show (shows "max+" when exceeded)
// color: 'primary' | 'secondary' | 'error' | 'warning' | 'success'
// size: 'small' | 'medium' | 'large'
// showZero: boolean - Whether to show when count is 0`}
        />

        {/* Tooltips */}
        <LiveEditor
          title="Tooltips & Help Text"
          description="Contextual tooltips for providing additional information and help text on hover or focus."
          component={TooltipExample}
          code={`import AutobiaTooltip, { AutobiaTooltipRich } from './components/DataDisplay/AutobiaTooltip';
import { Info, Star } from '@mui/icons-material';

const Example = () => (
  <Grid container spacing={3}>
    <Grid item xs={12}>
      {/* Basic Tooltips */}
      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
        <AutobiaTooltip title="This is a primary tooltip">
          <Button variant="contained">Hover me</Button>
        </AutobiaTooltip>
        
        <AutobiaTooltip title="Secondary color tooltip" color="secondary">
          <Button variant="outlined" color="secondary">Secondary</Button>
        </AutobiaTooltip>
        
        <AutobiaTooltip title="Light tooltip with border" color="light">
          <Button variant="outlined">Light</Button>
        </AutobiaTooltip>
      </Stack>
    </Grid>
    
    <Grid item xs={12}>
      {/* Tooltip Colors */}
      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
        <AutobiaTooltip title="Success message" color="success">
          <IconButton color="success"><Star /></IconButton>
        </AutobiaTooltip>
        
        <AutobiaTooltip title="Error notification" color="error">
          <IconButton color="error"><Info /></IconButton>
        </AutobiaTooltip>
      </Stack>
    </Grid>
    
    <Grid item xs={12}>
      {/* Tooltip Sizes & Placement */}
      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
        <AutobiaTooltip title="Small tooltip" size="small" placement="top">
          <Button size="small">Small</Button>
        </AutobiaTooltip>
        
        <AutobiaTooltip title="Large tooltip" size="large" placement="bottom">
          <Button size="large">Large</Button>
        </AutobiaTooltip>
      </Stack>
    </Grid>
    
    <Grid item xs={12}>
      {/* Rich Content Tooltip */}
      <AutobiaTooltipRich
        title="Order Information"
        subtitle="Click to view detailed order status and shipping information"
        color="primary"
      >
        <Button variant="contained" startIcon={<Info />}>
          Order Details
        </Button>
      </AutobiaTooltipRich>
    </Grid>
  </Grid>
);

// AutobiaTooltip Props:
// title: string | ReactNode - Tooltip content
// placement: 'top' | 'bottom' | 'left' | 'right' | 'top-start' | ... - Tooltip position
// arrow: boolean - Show arrow pointing to target
// color: 'dark' | 'light' | 'primary' | 'secondary' | 'success' | 'error' | 'warning'
// size: 'small' | 'medium' | 'large' - Tooltip size
// maxWidth: number - Maximum tooltip width
// enterDelay: number - Delay before showing (ms)
// leaveDelay: number - Delay before hiding (ms)
// disabled: boolean - Disable tooltip

// AutobiaTooltipRich Props:
// title: string - Main title
// subtitle: string - Secondary text
// color: string - Tooltip color theme`}
        />

        {/* Lists */}
        <LiveEditor
          title="Lists & Menu Components"
          description="Structured lists for displaying data, navigation menus, and interactive content with icons and actions."
          component={ListExample}
          code={`import AutobiaList, { AutobiaMenuList, AutobiaContactList, AutobiaSettingsList } from './components/DataDisplay/AutobiaList';

const menuItems = [
  { 
    id: 1, 
    icon: <ShoppingCart />, 
    primary: 'Orders', 
    secondary: 'Manage your orders', 
    navigatable: true 
  },
  { 
    id: 2, 
    icon: <Person />, 
    primary: 'Customers', 
    secondary: 'Customer management', 
    navigatable: true 
  },
];

const contacts = [
  { 
    id: 1, 
    name: 'Ahmed Al-Rashid', 
    email: 'ahmed@example.com', 
    status: 'Online' 
  },
  { 
    id: 2, 
    name: 'Sarah Johnson', 
    email: 'sarah@example.com', 
    status: 'Away' 
  },
];

const Example = () => (
  <Grid container spacing={3}>
    <Grid item xs={12} md={4}>
      <AutobiaMenuList items={menuItems} />
    </Grid>
    <Grid item xs={12} md={4}>
      <AutobiaContactList contacts={contacts} />
    </Grid>
  </Grid>
);

// AutobiaList Props:
// items: Array - List items with structure:
//   - id: string|number - Unique identifier
//   - icon: ReactNode - Leading icon
//   - avatar: string - Avatar image URL
//   - primary: string - Main text
//   - secondary: string - Secondary text
//   - trailing: string|ReactNode - End content
//   - action: ReactNode - Action button
//   - navigatable: boolean - Shows chevron
//   - onClick: function - Click handler
//   - children: Array - Nested items
// dense: boolean - Compact spacing
// dividers: boolean - Show dividers between items

// Specialized Variants:
// AutobiaMenuList - Navigation menus
// AutobiaContactList - Contact/user lists  
// AutobiaSettingsList - Settings pages`}
        />

        {/* Advanced Data Table */}
        <ComponentDemo
          title="Advanced Data Table"
          description="Feature-rich data table with search, selection, actions, animations, and export capabilities."
          component={<AdvancedTableExample />}
          usage={`import AutobiaDataTable from '../components/DataDisplay/AutobiaDataTable';
import { IoEyeOutline, IoCreateOutline, IoTrashOutline } from 'react-icons/io5';

const MyTable = () => {
  const [selectedRows, setSelectedRows] = useState([]);

  const columns = [
    { id: 'name', label: 'Customer Name', minWidth: 170 },
    { id: 'company', label: 'Company', minWidth: 150 },
    { 
      id: 'amount', 
      label: 'Amount', 
      type: 'currency',
      currency: 'SAR',
      align: 'right',
      minWidth: 120
    },
    { 
      id: 'status', 
      label: 'Status', 
      type: 'chip',
      chipColor: (value) => value === 'Active' ? 'success' : 'error',
      minWidth: 100
    },
    { id: 'date', label: 'Date', type: 'date', minWidth: 120 },
  ];

  const actions = [
    {
      icon: <IoEyeOutline />,
      label: 'View',
      onClick: (row) => console.log('View:', row),
      color: 'primary',
    },
    {
      icon: <IoCreateOutline />,
      label: 'Edit',
      onClick: (row) => console.log('Edit:', row),
      color: 'secondary',
    },
    {
      icon: <IoTrashOutline />,
      label: 'Delete',
      onClick: (row) => console.log('Delete:', row),
      color: 'error',
      disabled: (row) => row.status === 'Active',
    },
  ];

  return (
    <ResponsiveTableWrapper>
      <AutobiaDataTable
        data={myData}
        columns={columns}
        selectable={true}
        searchable={true}
        exportable={true}
        animated={true}
        actions={actions}
        pagination={true}
        onSelectionChange={setSelectedRows}
        onRowClick={(row) => console.log('Row clicked:', row)}
        maxHeight={500}
      />
    </ResponsiveTableWrapper>
  );
};`}
        />

        {/* Information Display */}
        <ComponentDemo
          title="Information Display"
          description="Structured information displays with multiple variants, types, and interactive features."
          component={<InformationDisplayExample />}
          usage={`import AutobiaInformationDisplay from '../components/DataDisplay/AutobiaInformationDisplay';

const MyInfoDisplay = () => {
  const orderInfo = [
    { label: 'Order ID', value: 'ORD-2024-001', type: 'id', copyable: true },
    { label: 'Customer', value: 'Ahmed Al-Rashid', type: 'user' },
    { label: 'Company', value: 'Tech Solutions Ltd', type: 'company' },
    { label: 'Amount', value: 15750.00, type: 'currency', currency: 'SAR' },
    { label: 'Status', value: 'Completed', type: 'status' },
    { label: 'Order Date', value: '2024-01-15T10:30:00Z', type: 'date' },
    { 
      label: 'Payment Link', 
      value: 'https://payment.example.com/pay/123', 
      type: 'link',
      onClick: (value) => window.open(value, '_blank')
    },
  ];

  return (
    <AutobiaInformationDisplay
      title="Order Information"
      data={orderInfo}
      animated={true}
      copyable={true}
      onCopy={(value) => console.log('Copied:', value)}
    />
  );
};

// Available variants: 'default' | 'compact' | 'grid'
// Available layouts: 'vertical' | 'horizontal'
// Available field types: 'id' | 'user' | 'company' | 'amount' | 'currency' | 'date' | 'status' | 'link' | 'list'`}
        />

        {/* Pagination */}
        <LiveEditor
          title="Pagination Components"
          description="Navigation pagination with page size controls, item counts, and multiple layout variants."
          component={PaginationExample}
          code={`import AutobiaPagination, { AutobiaSimplePagination, AutobiaTablePagination } from './components/Navigation/AutobiaPagination';

const [currentPage, setCurrentPage] = useState(1);
const [pageSize, setPageSize] = useState(10);
const totalItems = 250;
const totalPages = Math.ceil(totalItems / pageSize);

const Example = () => (
  <Grid container spacing={3}>
    {/* Complete Pagination */}
    <Grid item xs={12}>
      <AutobiaPagination
        count={totalPages}
        page={currentPage}
        onChange={(e, page) => setCurrentPage(page)}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        totalItems={totalItems}
        showFirstLast={true}
        showInfo={true}
        showPageSize={true}
      />
    </Grid>
    
    {/* Simple Pagination */}
    <Grid item xs={12}>
      <AutobiaSimplePagination
        count={totalPages}
        page={currentPage}
        onChange={(e, page) => setCurrentPage(page)}
      />
    </Grid>

    {/* Table Pagination */}
    <Grid item xs={12}>
      <AutobiaTablePagination
        count={totalPages}
        page={currentPage}
        onChange={(e, page) => setCurrentPage(page)}
        totalItems={totalItems}
        pageSize={pageSize}
      />
    </Grid>
  </Grid>
);

// AutobiaPagination Props:
// count: number - Total number of pages
// page: number - Current page (1-based)
// onChange: function - Page change handler
// pageSize: number - Items per page
// onPageSizeChange: function - Page size change handler
// totalItems: number - Total number of items
// showFirstLast: boolean - Show first/last page buttons
// showInfo: boolean - Show item count info
// showPageSize: boolean - Show page size selector
// pageSizeOptions: Array<number> - Available page sizes
// disabled: boolean - Disabled state
// size: 'small' | 'medium' | 'large' - Button size
// variant: 'outlined' | 'text' - Button style
// shape: 'rounded' | 'circular' - Button shape`}
        />
      </Stack>

      {/* Usage Guidelines */}
      <Box sx={{ mt: 8, p: 4, backgroundColor: 'gray.g25', borderRadius: 2 }}>
        <Typography variant="h4" fontWeight={600} sx={{ mb: 3 }}>
          Data Display Guidelines
        </Typography>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
              Numeric Display
            </Typography>
            <Typography variant="body2" color="gray.g600" sx={{ mb: 2 }}>
              Use NumericIconCard for financial metrics and KPIs. Always include 
              relevant icons and proper thousand separators for large numbers.
            </Typography>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
              Table Organization
            </Typography>
            <Typography variant="body2" color="gray.g600">
              Keep table columns concise and prioritize the most important data. 
              Use consistent column widths and proper text alignment.
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
              Loading States
            </Typography>
            <Typography variant="body2" color="gray.g600" sx={{ mb: 2 }}>
              Always provide loading indicators for data that takes time to fetch. 
              Use skeleton loaders for better perceived performance.
            </Typography>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
              Responsive Design
            </Typography>
            <Typography variant="body2" color="gray.g600">
              Ensure data tables scroll horizontally on mobile devices. 
              Stack cards vertically on smaller screens for better readability.
            </Typography>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default DataDisplay;